'use client'
// React Alert Container Component
import React, { memo, useEffect, useState } from "react";
import { Button, Checkbox } from "./";
import Modal from "@/components/Modal/Modal";


type AlertProps = {
    title?: string | React.ReactNode | null;
    message?: string | React.ReactNode | null;
    button?: string;
    cancel?: string;
    className?: string;
    confirm?: string | null;
    isConfirmed?: boolean;
    isVisible?: boolean;
    callBack?: (okBtn: boolean) => void;
    onConfirm?: (conformation: boolean) => void;
    isCancelable?: boolean;
    danger?: boolean;
};

type AlertListener = (props: AlertProps) => void;

class AlertService {
    private defaultAlertProps: AlertProps;
    private alertProps: AlertProps;
    private listeners: AlertListener[];

    constructor() {
        this.defaultAlertProps = {
            title: null,
            message: null,
            button: "Ok!",
            cancel: "Cancel",
            className: "",
            confirm: null,
            isConfirmed: false,
            isVisible: false,
            callBack: () => {},
            onConfirm: () => {},
            isCancelable: false,
            danger: false,
        };
        this.alertProps = { ...this.defaultAlertProps };
        this.listeners = [];
    }

    showAlert(props: Partial<AlertProps>) {
        this.alertProps = { ...this.alertProps, ...props, isVisible: true };
        this.notifyListeners();
    }

    hideAlert() {
        this.alertProps = { ...this.defaultAlertProps, isVisible: false };
        this.notifyListeners();
    }

    confirmAlert(conformation: boolean = true) {
        this.alertProps = { ...this.alertProps, isConfirmed: conformation };
        this.notifyListeners();
    }

    registerListener(listener: AlertListener) {
        this.listeners.push(listener);
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener(this.alertProps));
    }
}

// Creating a singleton instance
const alertService = new AlertService();
export default alertService;


const AlertContainer: React.FC = memo(() => {
    const [alertProps, setAlertProps] = useState<AlertProps | undefined>(undefined);

    const {
        title,
        message,
        button = "Ok!",
        cancel = "Cancel",
        confirm,
        className,
        callBack,
        onConfirm,
        isConfirmed,
        isVisible = false,
        isCancelable,
        danger,
    } = alertProps || {};

    useEffect(() => {
        const updateProps = (props: AlertProps) => setAlertProps({ ...props });
        alertService.registerListener(updateProps);
    }, []);

    const handleOk = () => {
        if (callBack) {
            callBack(true);
        }
        alertService.hideAlert();
    };

    const handleCancel = () => {
        if (callBack) {
            callBack(false);
        }
        alertService.hideAlert();
    };

    const handleConfirm = (state: boolean) => {
        if (onConfirm) {
            onConfirm(state);
        }
        alertService.confirmAlert(state);
    };

    //if (!isVisible) return null;

    return (
        <Modal isOpen={isVisible} onClose={handleCancel} containerClassName={"!z-[1000]"} titleClassName={"hidden"} className={className || ""} >

                <div className={`flex flex-col gap-xxl w-[400px]`}>

                    <div className={'text-text-md gap-sm flex flex-col pt-md '}>
                        <span className={"w-full block text-title-xl text-gray-900 text-center"}>{title}</span>
                        <span className={"w-full block text-text-md  text-gray-900 text-center"}>{message}</span>
                        {confirm && (
                            <div className={`py-md rounded-md`}>
                                <Checkbox label={confirm} onChange={handleConfirm} defaultChecked={isConfirmed} />
                            </div>
                        )}
                    </div>
                    <div className={'flex flex-row gap-md justify-center'}>
                        {isCancelable && (
                            <Button type={"alt"} className={'flex-grow flex-1 inset-1'} onClick={handleCancel}>
                                {cancel}
                            </Button>
                        )}
                        <Button

                            className={
                                `${isCancelable ? "flex-grow flex-1 inset-1" : " min-w-[150px]"} ` +
                                `${danger ? "bg-red-500 border-red-600 hover:bg-red-600  active:bg-red-600 focus:bg-red-600 focus:border-red-600 active:border-red-600 active:shadow-red" : ""}`
                            }
                            onClick={handleOk}
                            disabled={confirm ? !isConfirmed : false}
                        >
                            {button}
                        </Button>
                    </div>
                </div>


        </Modal>

    );
});

AlertContainer.displayName = 'AlertContainer';
export {AlertContainer};
